import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutButton from "../components/LogoutButton";
import "../style/executivereport.css";

// Function to format date as DD-MMM-YYYY
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return date.toLocaleDateString("en-GB", options).replace(",", "");
};

// Function to format amount with commas and two decimal places
const formatAmount = (amount) => {
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(amount);
};

function ExecutiveReport() {
  const navigate = useNavigate();
  
  // Sample payment collection data (Sorted by latest date first)
  const [payments] = useState([
    { date: "2025-03-22", amount: 1000, method: "Card", collectedBy: "Nikhil" },
    { date: "2025-03-21", amount: 2000, method: "Online", collectedBy: "Tanmay" },
    { date: "2025-03-20", amount: 1500, method: "Cash", collectedBy: "Sparsh" },
    { date: "2025-03-19", amount: 3000, method: "UPI", collectedBy: "Amit" },
    { date: "2025-03-18", amount: 1200, method: "Cheque", collectedBy: "Rahul" },
    { date: "2025-03-17", amount: 2200, method: "Bank Transfer", collectedBy: "Vikas" },
  ]);

  const [filterDate, setFilterDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 5; // Change this to modify number of records per page

  // Filter payments based on date and search query
  const filteredPayments = payments
    .filter((payment) => !filterDate || payment.date === filterDate)
    .filter((payment) =>
      Object.values(payment).some((value) =>
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    );

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredPayments.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredPayments.length / recordsPerPage);

  // Export to CSV function
  const exportToCSV = () => {
    const csvRows = [
      ["Date", "Amount (₹)", "Payment Method", "Collected By"],
      ...filteredPayments.map((p) => [formatDate(p.date), formatAmount(p.amount), p.method, p.collectedBy]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvRows], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payment_collection_report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="executive-report-container">
      {/* Navbar */}
      <nav className="executive-report-navbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px" }}>
        <div className="executive-report-logo">📊 Reports</div>
        <LogoutButton />
      </nav>

      {/* Report Content */}
      <div className="executive-report-content">
        <h2 className="executive-report-title">Payment Collection Report</h2>

        {/* Filters */}
        <div className="executive-report-filter">
          <label>Select Date: </label>
          <input
            type="date"
            className="executive-report-input"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />
          <input
            type="text"
            className="executive-report-search"
            placeholder="Search by any field..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="executive-report-btn" onClick={exportToCSV}>
            Export CSV
          </button>
        </div>

        {/* Payment Table */}
        <table className="executive-report-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount (₹)</th>
              <th>Payment Method</th>
              <th>Collected By</th>
            </tr>
          </thead>
          <tbody>
            {currentRecords.length > 0 ? (
              currentRecords.map((payment, index) => (
                <tr key={index}>
                  <td>{formatDate(payment.date)}</td>
                  <td>₹{formatAmount(payment.amount)}</td>
                  <td>{payment.method}</td>
                  <td>
                    <a href={`/user/${payment.collectedBy.toLowerCase()}`} className="clickable-link">
                      {payment.collectedBy}
                    </a>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="executive-report-no-data">No records found</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              ⬅ Prev
            </button>
            <span> Page {currentPage} of {totalPages} </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next ➡
            </button>
          </div>
        )}
      </div>

      {/* Footer Navigation */}
      <footer className="admin-dashboard-footer">
        <button className="admin-dashboard-nav-btn" onClick={() => navigate("/executive")}>
          Home
        </button>
        <button className="admin-dashboard-nav-btn" onClick={() => navigate("/executivereport")}>
          Reports
        </button>
      </footer>
    </div>
  );
}

export default ExecutiveReport;
